// Production calendar: date helpers, role checks and copy helpers shared by its tabs and dialogs.
import { clean } from '../../shell/copy';
import { cardTitle } from '../../lib/cardTitle';

export function dateKey(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// Every day of the month's weeks, Sunday first (days of the previous and next month fill the first and last week).
export function buildCalDays(year, month) {
  const first = new Date(year, month, 1);
  const last  = new Date(year, month + 1, 0);
  const start = new Date(first);
  start.setDate(first.getDate() - first.getDay());
  const end = new Date(last);
  end.setDate(last.getDate() + (6 - last.getDay()));
  const days = [];
  const cur  = new Date(start);
  while (cur <= end) { days.push(new Date(cur)); cur.setDate(cur.getDate() + 1); }
  return days;
}

export function getWeekMonday(fromDate) {
  const d = new Date(fromDate);
  d.setHours(0, 0, 0, 0);
  const day = d.getDay(); // 0 = Sun
  d.setDate(d.getDate() - (day === 0 ? 6 : day - 1));
  return d;
}

export const MONTH_NAMES    = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
export const DAY_NAMES      = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
export const WEEK_DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export const TIME_OPTIONS = [];
for (let h = 6; h <= 23; h++) {
  for (const m of [0, 30]) {
    if (h === 23 && m === 30) break;
    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12  = h % 12 || 12;
    TIME_OPTIONS.push({ value: `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`, label: `${h12}:${String(m).padStart(2, '0')} ${ampm}` });
  }
}
export const SCHEDULE_SLOTS = TIME_OPTIONS.filter(o => o.value >= '07:00' && o.value <= '21:00');

export function fmtTime12(t) {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${ampm}`;
}

// 'YYYY-MM-DD' → "Monday, October 19" (optionally with the year)
export function prettyDate(key, withYear = false) {
  const [y, m, d] = key.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', ...(withYear ? { year: 'numeric' } : {}) });
}

export function shortDate(key) {
  if (!key) return '';
  const [y, m, d] = key.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

// Card title for display: "Series, Ep N" for an episode card (cardTitle joins them with a middle dot).
export const recTitle = rec => clean(cardTitle(rec));

// A talent on a segment, by name: the registry name, else the free-form display name.
export const talentName = t => clean(t?.talent?.name || t?.talentDisplayName || '');

// Compact initials for a segment's talent list: stored initials, else two letters from the display name.
export function segmentInitialsList(segment) {
  const out = [];
  for (const t of (segment?.talents || [])) {
    if (t.talent?.initials) { out.push(t.talent.initials); continue; }
    const name = t.talentDisplayName?.trim();
    if (!name) continue;
    const parts = name.split(/\s+/).filter(Boolean);
    out.push(parts.length >= 2 ? (parts[0][0] + parts[1][0]).toUpperCase() : parts[0].slice(0, 2).toUpperCase());
  }
  return out;
}

// A broken booking, in words: available that day but outside their window, or not available that day at all.
export function coverageReason(name, dayLabel, cov) {
  if (cov?.dayAvailType && cov.shootTimeSlot) {
    const win = cov.dayAvailType === 'full_day' ? 'all day' : `${fmtTime12(cov.dayBlockStart)} to ${fmtTime12(cov.dayBlockEnd)}`;
    return `${name} is available ${dayLabel} but not at ${fmtTime12(cov.shootTimeSlot)}: their window is ${win}`;
  }
  return `${name} is not available ${dayLabel}`;
}

// Role checks (HD-6 step 0d gates, unchanged).
export function canSeeTalentAvailability(user) {
  const r = user?.roles || [];
  return r.some(x => ['Admin', 'Power User', 'Producer', 'Writer', 'Qual Reviewer', 'Qual_Reviewer'].includes(x));
}
// The Script List and day notes (/api/tal reads): the API's TAL_READ_ROLES, never an external account.
export function canReadScriptList(user) {
  if (user?.isExternal) return false;
  const r = user?.roles || [];
  return r.some(x => ['Admin', 'Power User', 'Power_User', 'Producer', 'Writer', 'Qual Reviewer', 'Qual_Reviewer'].includes(x));
}
export function canAssignTalent(user) {
  const r = user?.roles || [];
  return r.some(x => ['Admin', 'Power User', 'Producer'].includes(x));
}
export function canReorderShoots(user) {
  const r = user?.roles || [];
  return r.some(x => ['Admin', 'Power User', 'Producer', 'Power_User'].includes(x));
}

// Every scheduled segment of the shoot records, by day, in the day's sort order.
export function segmentsByDate(records) {
  const map = {};
  for (const rec of records) {
    for (const segment of (rec.shootSegments || [])) {
      if (!segment.shootDay) continue;
      const k = segment.shootDay.slice(0, 10);
      if (!map[k]) map[k] = [];
      map[k].push({ rec, segment });
    }
  }
  for (const k of Object.keys(map)) map[k].sort((a, b) => (a.segment.sortOrder ?? 0) - (b.segment.sortOrder ?? 0));
  return map;
}

// A click on a Calendar cell that holds controls (chips, buttons): CalendarCell turns into a <button> when given
// onClick, which cannot hold buttons, so these cells take the click in the capture phase and leave clicks that land
// on a control inside to that control.
export const cellClick = handler => e => {
  if (e.target.closest('button, a, input, select, textarea')) return;
  handler(e);
};
